import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Card, CardContent } from '@/components/ui/card';
import { Lightbox } from '@/components/site/Lightbox';
import { publicApi } from '@/lib/api';
import { absoluteImageUrl } from '@/components/admin/ImageUploader';
import { FIELD_NOTE_CATEGORIES, formatDate, labelFor, youtubeEmbedUrl } from '@/lib/taxonomies';
import { useSeo } from '@/lib/seo';

export default function FieldNoteDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    publicApi
      .fieldNoteDetail(slug)
      .then(setData)
      .catch(() => setNotFound(true));
  }, [slug]);

  const note = data?.note;
  useSeo({
    title: note ? `${note.seo_title || note.title} | Field Notes | A Guy Who Hunts` : 'Field Notes',
    description: note?.seo_description || note?.excerpt,
    ogImage: note?.hero_image?.url,
    type: 'article',
  });

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-20 text-center">
        <p className="kicker">404</p>
        <h1 className="font-serif text-4xl mt-2">Note not found.</h1>
        <Link to="/field-notes" className="mt-6 inline-block underline">
          Back to Field Notes
        </Link>
      </div>
    );
  }

  if (!note) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="h-6 w-40 bg-[hsl(var(--muted))] animate-pulse rounded" />
        <div className="mt-6 h-10 w-3/4 bg-[hsl(var(--muted))] animate-pulse rounded" />
      </div>
    );
  }

  const embed = note.youtube_url ? youtubeEmbedUrl(note.youtube_url) : null;

  return (
    <article>
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pt-8">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/field-notes">Field Notes</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="line-clamp-1">{note.title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <header className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 mt-6">
        <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
          <Badge variant="secondary" className="font-normal">
            {labelFor(FIELD_NOTE_CATEGORIES, note.category)}
          </Badge>
          <span>•</span>
          <time>{formatDate(note.date)}</time>
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight tracking-tight mt-3">{note.title}</h1>
        {note.excerpt && (
          <p className="mt-4 text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">{note.excerpt}</p>
        )}
      </header>

      {note.hero_image?.url && (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 mt-8">
          <Card className="overflow-hidden border-[hsl(var(--border))] card-elev">
            <AspectRatio ratio={16 / 9}>
              <img
                src={absoluteImageUrl(note.hero_image.url)}
                alt={note.hero_image.alt || note.title}
                className="h-full w-full object-cover"
              />
            </AspectRatio>
          </Card>
        </div>
      )}

      <div className="mx-auto max-w-[72ch] px-4 sm:px-6 lg:px-8 py-12 prose-editorial">
        {note.body?.split(/\n\n+/).map((p, i) => <p key={i}>{p}</p>)}
      </div>

      {embed && (
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pb-14">
          <Card className="overflow-hidden border-[hsl(var(--border))] card-elev">
            <AspectRatio ratio={16 / 9}>
              <iframe src={embed} title={note.title} className="h-full w-full" allowFullScreen loading="lazy" />
            </AspectRatio>
          </Card>
        </div>
      )}

      {note.gallery?.length > 0 && (
        <section className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pb-14">
          <div className="mb-5 flex items-baseline justify-between gap-4">
            <p className="kicker">Photo essay</p>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              {note.gallery.length} image{note.gallery.length === 1 ? '' : 's'}
            </p>
          </div>
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {note.gallery.map((img, i) => (
              <figure
                key={`${img.url}-${i}`}
                className="rounded-2xl overflow-hidden border border-[hsl(var(--border))] bg-[hsl(var(--card))]"
              >
                <button
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
                  aria-label={`Open image ${i + 1} of ${note.gallery.length} in full-screen`}
                  data-testid="field-note-gallery-image"
                >
                  <AspectRatio ratio={4 / 3}>
                    <img
                      src={absoluteImageUrl(img.url)}
                      alt={img.alt || `Photo ${i + 1} in ${note.title}`}
                      className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.02]"
                      loading="lazy"
                    />
                  </AspectRatio>
                </button>
                {img.alt && (
                  <figcaption className="px-3 py-2 text-xs text-[hsl(var(--muted-foreground))] line-clamp-2">
                    {img.alt}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
          {lightboxIndex !== null && (
            <Lightbox
              images={note.gallery}
              index={lightboxIndex}
              onIndexChange={setLightboxIndex}
              onClose={() => setLightboxIndex(null)}
            />
          )}
        </section>
      )}

      {data?.related?.length > 0 && (
        <section className="bg-[hsl(var(--secondary)/0.55)] border-t border-[hsl(var(--border))]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14">
            <p className="kicker">Related notes</p>
            <h2 className="font-serif text-2xl sm:text-3xl mt-2 tracking-tight">More from this category.</h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {data.related.map((r) => (
                <Link key={r.slug} to={`/field-notes/${r.slug}`} className="group">
                  <Card className="h-full overflow-hidden border-[hsl(var(--border))] card-elev card-elev-hover">
                    <AspectRatio ratio={4 / 3}>
                      <img
                        src={absoluteImageUrl(r.hero_image?.url)}
                        alt={r.hero_image?.alt || r.title}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </AspectRatio>
                    <CardContent className="p-5">
                      <h3 className="font-serif text-lg tracking-tight">{r.title}</h3>
                      <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] line-clamp-2">{r.excerpt}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
